import { TFunction } from 'next-i18next';
import { useJobListHistoryAndURL } from '@hooks/history/useJobListHistoryAndURL';
import { InfinitePageType, PaginationPageType } from '@type/swr';
import { useBaseFiltersQueryParamsState } from './useBaseFiltersQueryParamsState';

/** Props */
interface UseFiltersQueryParamsJobListProps<T extends InfinitePageType = 'page'> {
  serverQueryParams?: object,
  initialPage?: PaginationPageType<T>,
  keepAsCommaSeparatedValues?: string[],
  sortQueryParams?: boolean,
  t: TFunction,
  paginationType?: T,
}

/**
 * @description A hook to manage the query params for the job list page (Pages Router).
 * It leverages `useBaseFiltersQueryParamsState` for state management and adds
 * job-list-specific browser history updates via `useJobListHistoryAndURL`.
 *
 * Use this hook on the job list page instead of the generic `useFiltersQueryParams`.
 * For all other paginated lists use `useFiltersQueryParams`.
 *
 * Params:
 * - `serverQueryParams`: *object* - the query params from the server
 * - `initialPage`: *string | number* - the initial page
 * - `keepAsCommaSeparatedValues`: *string[]* - keys that should be kept as comma-separated values
 * - `sortQueryParams`: *boolean* - if true, query params are sorted alphabetically. Default true.
 * - `t`: *TFunction* - translation function (required for locale-aware slug URL building).
 * - `paginationType`: *InfinitePageType* - the type of pagination used. Default is `'page'`.
 *
 * Returns:
 * - `queryParams`: *object* - the current query params
 * - `setQueryParams`: *function* - a function to update the query params
 * - `currentPage`: *string | number* - the current page / cursor
 * - `setCurrentPage`: *function* - a function to update the current page / cursor
 *
 * @example
 * const {
 *   queryParams,
 *   setQueryParams,
 *   setCurrentPage,
 * } = useFiltersQueryParamsJobList({
 *   serverQueryParams: objFilterParams,
 *   initialPage: cursor,
 *   t,
 *   paginationType: 'cursor',
 * });
 */
export const useFiltersQueryParamsJobList = <T extends InfinitePageType = 'page'>(
  props: UseFiltersQueryParamsJobListProps<T>,
) => {
  const {
    serverQueryParams,
    initialPage,
    keepAsCommaSeparatedValues,
    sortQueryParams = true,
    t,
    paginationType = 'page' as T,
  } = props;

  // State management
  const {
    queryParams,
    setQueryParams,
    currentPage,
    setCurrentPage,
  } = useBaseFiltersQueryParamsState<T>({
    serverQueryParams,
    initialPage,
    keepAsCommaSeparatedValues,
    sortQueryParams,
    paginationType,
  });

  // Job-list-specific history and URL management
  useJobListHistoryAndURL(currentPage, queryParams, setQueryParams, t, paginationType);

  return {
    queryParams,
    setQueryParams,
    currentPage,
    setCurrentPage,
  };
};
